import type { Frame } from "@/api/spaces";

type EditableFrameFields = Partial<Pick<Frame, "content" | "title">>;

/**
 * Maps content to the part that identifies a user edit. Docs drop the
 * server-owned `revision`, so a save and its echo compare equal although the
 * server bumped the revision.
 */
export type ComparableContent = (
  content: Record<string, unknown>,
) => Record<string, unknown>;

interface ProtectedEdit extends EditableFrameFields {
  comparable?: ComparableContent;
  supersededContents: unknown[];
  supersededTitles: string[];
  expiresAt: number;
}

const LOCAL_EDIT_TTL_MS = 10_000;
const protectedEdits = new Map<string, ProtectedEdit>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Objects with sorted keys: Postgres `jsonb` returns keys in its own order. */
function sortedKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortedKeys);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, sortedKeys(value[key])]),
  );
}

function sameJson(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  try {
    return (
      JSON.stringify(sortedKeys(left)) === JSON.stringify(sortedKeys(right))
    );
  } catch {
    return false;
  }
}

/**
 * Whether two frame contents are the same edit, ignoring key order and,
 * with `comparable`, the fields it drops (a Doc's server-owned revision).
 */
export function sameFrameContent(
  left: unknown,
  right: unknown,
  comparable?: ComparableContent,
): boolean {
  if (!comparable || !isRecord(left) || !isRecord(right)) {
    return sameJson(left, right);
  }
  return sameJson(comparable(left), comparable(right));
}

/** Remember the newest optimistic user edit while REST broadcasts catch up. */
export function rememberLocalFrameEdit(
  frameId: string,
  edit: EditableFrameFields,
  comparable?: ComparableContent,
): void {
  const existing = protectedEdits.get(frameId);
  const supersededContents = [...(existing?.supersededContents ?? [])];
  const supersededTitles = [...(existing?.supersededTitles ?? [])];
  if (
    edit.content !== undefined &&
    existing?.content !== undefined &&
    !sameFrameContent(edit.content, existing.content, comparable)
  ) {
    supersededContents.push(existing.content);
  }
  if (
    edit.title !== undefined &&
    existing?.title !== undefined &&
    edit.title !== existing.title
  ) {
    supersededTitles.push(existing.title);
  }
  protectedEdits.set(frameId, {
    ...existing,
    ...edit,
    comparable,
    supersededContents: supersededContents.slice(-8),
    supersededTitles: supersededTitles.slice(-8),
    expiresAt: Date.now() + LOCAL_EDIT_TTL_MS,
  });
}

/**
 * Ignore only echoes whose value matches a known superseded local snapshot.
 * Unrecognized user edits (for example, another browser) and agent/MCP writes
 * remain authoritative, so collaboration is not paused.
 */
export function protectLocalFrameEdit<T extends Partial<Frame>>(
  frameId: string,
  update: T,
): T {
  const expected = protectedEdits.get(frameId);
  if (!expected) return update;
  if (expected.expiresAt <= Date.now()) {
    protectedEdits.delete(frameId);
    return update;
  }
  if (!update.edited_by_user_at) return update;

  let next: T | null = null;
  if (
    update.content !== undefined &&
    expected.content !== undefined &&
    !sameFrameContent(update.content, expected.content, expected.comparable) &&
    expected.supersededContents.some((content) =>
      sameFrameContent(update.content, content, expected.comparable),
    )
  ) {
    next = { ...update };
    delete next.content;
  }
  if (
    update.title !== undefined &&
    expected.title !== undefined &&
    update.title !== expected.title &&
    expected.supersededTitles.includes(update.title)
  ) {
    next = next ?? { ...update };
    delete next.title;
  }
  return next ?? update;
}

export function clearLocalFrameEdits(): void {
  protectedEdits.clear();
}
